import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field, TextInput, SelectInput, ActionButton, EmptyState } from "./common";

const EMPTY = { catalog_item_id: "", uom: "", rate: "", notes: "" };

export const CustomerRates = ({ customerId }) => {
  const { user } = useAuth();
  const canWrite = ["admin", "dispatcher"].includes(user?.role);
  const [rates, setRates] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/customer-rates?customer_id=${customerId}`),
      api.get("/catalog-items"),
    ])
      .then(([r, c]) => { setRates(r.data); setCatalog(c.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (customerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const catalogOptions = useMemo(
    () => catalog.filter((c) => c.active !== false).map((c) => ({ value: c.id, label: c.name })),
    [catalog]
  );

  const startAdd = () => { setForm(EMPTY); setEditingId(null); setAdding(true); };
  const startEdit = (r) => {
    setForm({ catalog_item_id: r.catalog_item_id, uom: r.uom || "", rate: r.rate ?? "", notes: r.notes || "" });
    setEditingId(r.id); setAdding(false);
  };
  const cancel = () => { setAdding(false); setEditingId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.catalog_item_id) { toast.error("Pick an item"); return; }
    setBusy(true);
    const payload = {
      uom: form.uom || null,
      rate: form.rate === "" ? null : Number(form.rate),
      notes: form.notes || null,
    };
    try {
      if (editingId) {
        await api.patch(`/customer-rates/${editingId}`, payload);
        toast.success("Rate updated");
      } else {
        await api.post("/customer-rates", { customer_id: customerId, catalog_item_id: form.catalog_item_id, ...payload });
        toast.success("Rate added");
      }
      cancel(); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this rate?")) return;
    try { await api.delete(`/customer-rates/${id}`); toast.success("Rate removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const fmtRate = (r) => (r.effective_rate != null ? `$${Number(r.effective_rate).toLocaleString()}` : "—");

  return (
    <div data-testid="customer-rates-tab" className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">Item rates specific to this customer. Blank rate falls back to the catalog default.</p>
        {canWrite && !adding && !editingId && (
          <ActionButton testId="customer-rate-add" onClick={startAdd} className="!px-3 !py-1.5"><Plus size={14} /> Add Rate</ActionButton>
        )}
      </div>

      {(adding || editingId) && (
        <div data-testid="customer-rate-form" className="rounded-md border border-slate-700 bg-slate-900/60 p-3 space-y-3">
          <Field label="Item">
            {editingId ? (
              <div className="text-sm text-slate-200 py-1">{rates.find((x) => x.id === editingId)?.item_name}</div>
            ) : (
              <SelectInput testId="customer-rate-item-select" value={form.catalog_item_id}
                onChange={(e) => setForm({ ...form, catalog_item_id: e.target.value })}
                options={catalogOptions} placeholder="Select catalog item…" className="!bg-slate-900" />
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Unit of Measure">
              <TextInput testId="customer-rate-uom-input" value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="Each / Hour / Day / BBL" />
            </Field>
            <Field label="Rate ($)">
              <TextInput testId="customer-rate-rate-input" type="number" min="0" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
            </Field>
          </div>
          <Field label="Notes">
            <TextInput testId="customer-rate-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="flex gap-2">
            <ActionButton testId="customer-rate-save" onClick={save} disabled={busy} className="!px-3 !py-1.5"><Check size={14} /> {busy ? "Saving…" : "Save"}</ActionButton>
            <ActionButton testId="customer-rate-cancel" variant="secondary" onClick={cancel} className="!px-3 !py-1.5"><X size={14} /> Cancel</ActionButton>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500" data-testid="customer-rates-loading">Loading rates…</p>
      ) : rates.length === 0 ? (
        <EmptyState message="No rates set for this customer yet" testId="customer-rates-empty" />
      ) : (
        <div className="rounded-md border border-slate-700/60 overflow-hidden" data-testid="customer-rates-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/60 bg-slate-900/60 text-xs font-mono uppercase tracking-wider text-slate-400">
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Unit</th>
                <th className="text-left px-3 py-2">Rate</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/60" data-testid={`customer-rate-row-${r.id}`}>
                  <td className="px-3 py-2 text-slate-100">{r.item_name || "—"}{r.item_type ? <span className="text-slate-500"> · {r.item_type}</span> : null}</td>
                  <td className="px-3 py-2 text-slate-400">{r.effective_uom || "—"}{r.rate == null && r.default_rate != null ? <span className="text-amber-400/70 text-xs"> (default)</span> : null}</td>
                  <td className="px-3 py-2 text-slate-300">{fmtRate(r)}</td>
                  <td className="px-3 py-2 text-right">
                    {canWrite && (
                      <div className="flex gap-1 justify-end">
                        <button data-testid={`customer-rate-edit-${r.id}`} onClick={() => startEdit(r)} className="p-1.5 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10"><Pencil size={13} /></button>
                        <button data-testid={`customer-rate-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
